import * as XLSX from "xlsx";
import { AlertData } from "../../../../shared/models/alert";
import { AppType } from "../app.type";
import { Manufacturer, SVHCItem } from "../../../../shared/models/manufacturer";
import { Part } from "../../../../shared/models/part";
import { Post } from "../../../../shared/models/post";
import { AuditLog } from "../../../../shared/models/auditLog";
import { StringUtils } from "../../../../shared/utils/string.utils";
import { Task } from "../../../../shared/models/task";
import {
  filterManufacturersForExport,
  filterPartsForExport,
  getManufacturerIdsByMode,
  getManufacturers,
  getManufacturersByCPNs,
  getParts,
  getProactiveItems,
  getEngineeringParts,
  getAllPartsByStatus,
  getPartsNotUsedInAssembliesAll,
  getPartsToBeDeletedAll,
  getPartsWithoutManufacturersAll,
  getAuditLogs,
} from "../api.service";
import { Customer } from "../../../../shared/types/customers";
import { COMMENT, SOLUTION, TASK } from "../post/post.service.type";
import { formatDatetime } from "../utils/date.util";
import { UserCompressed } from "../../../../shared/models/user";
import { Alert } from "../../../../shared/types/alerts";
import { applyStatusColors } from "./export-status-color";
import { writeStyledFile } from "./excel-cell-style";
import { WITHOUT_DATA } from "../filter-tree/filter-tree.service";
import { FieldTypes } from "../../../../shared/settings/settings";

export async function writeConfigFile(
  app: AppType,
  docType: string,
  exportType?: string,
) {
  const { book, fileName } = await createBook(app, docType, exportType);
  writeStyledFile(book, fileName);
}

export async function createBook(
  app: AppType,
  docType: string,
  exportType?: string,
) {
  const book = XLSX.utils.book_new();
  let name = "report.xlsx";
  switch (docType) {
    case "manufacturer":
      XLSX.utils.book_append_sheet(
        book,
        await getExportedManufacturers(app, exportType),
      );
      name = generateRMExportFileName(app, exportType);
      break;
    case "part":
      const part = app.part.part;
      let result: XLSX.WorkSheet;
      if (exportType === "proactiveItems") {
        result = await getExportedProactiveItems(app);
        name = generateAssemblyReportFileName(app, app.text.RM.clientReport);
      } else if (exportType === "engineeringItems") {
        result = await getExportedEngineeringParts(app);
        name = generateAssemblyReportFileName(
          app,
          app.text.RM.proactiveReportEngineering,
        );
      } else if (app.RM.isPart) {
        result = await getExportedParts(app, app.partsTable.parts);
        name = generateExportNameForParts(app, part);
      } else {
        result = await getExportedBillOfMaterial(app, exportType);
        name = generateExportNameForParts(app, part);
      }
      XLSX.utils.book_append_sheet(book, result);
      break;

    case Alert.ALERT:
    case Alert.CHANGEALERT:
    case Alert.LEADTIMEALERT:
    case Alert.INVENTORYMONITORINGALERT:
    case Alert.ENVCOMPLIANCEALERT:
      XLSX.utils.book_append_sheet(book, await getExportedAlerts(app, docType));
      name = generateAlertsExportFileName(app, docType);
      break;
    case TASK:
      XLSX.utils.book_append_sheet(book, await getExportedTasks(app));
      name = generateOMExportFileName(app);
      break;
    case "post":
      XLSX.utils.book_append_sheet(book, await getExportedPosts(app));
      break;
    case "user":
      XLSX.utils.book_append_sheet(book, await getExportedUsers(app));
      break;
    case "auditLog":
      XLSX.utils.book_append_sheet(book, await getExportedAuditLogs(app));
      break;
  }
  return { book, fileName: name };
}

/** Prepare the RM data from bill of material section for export */
async function getExportedBillOfMaterial(app: AppType, exportType?: string) {
  const assemblies = app.part.currentAssemblies;
  const subPartsWithManufacturers: Part[] = [];
  const subPartsWithParts: Part[] = [];
  let result: XLSX.WorkSheet;
  assemblies.forEach((assembly) => {
    if (assembly.parts == null || Object.keys(assembly.parts).length == 0) {
      subPartsWithManufacturers.push(assembly);
    } else {
      subPartsWithParts.push(assembly);
    }
  });

  if (subPartsWithManufacturers.length > 0) {
    result = await getExportedBillOfMatWithManufacturers(
      app,
      subPartsWithManufacturers,
      subPartsWithParts,
      exportType,
    );
  } else {
    result = await getExportedParts(app, assemblies);
  }
  return result;
}

/** Exports the RM data as seen in the manufacturers list views based on exportType */
async function getExportedManufacturers(app: AppType, exportType?: string) {
  let manufacturerIds: any[] = app.manufacturersFilter.currentIds;
  let manufacturers: Manufacturer[] = [];

  const assemblySelected = app.filterTree.assemblySelected;
  const filterSelected = app.filterHeaderTable.isFilterSelected;

  const mode = app.RM.mode;

  // if (Number(app.manufacturersFilter.pages) !== manufacturerIds.length) {
  if (StringUtils.isNullOrEmpty(assemblySelected)) {
    // if you are not on a mini tree table get the manufacturer ids based on the RM mode
    if (!filterSelected) {
      // if there is no selected filter on the table head
      manufacturerIds = await getManufacturerIdsByMode(
        app.customers.expectCurrent,
        mode,
      );
      manufacturers = await getManufacturers(
        app.customers.expectCurrent,
        manufacturerIds,
      );
    }
    if (filterSelected) {
      // if there a filter selected on the table head
      manufacturerIds = app.filterHeaderTable.MPNIds;
      manufacturers = await getManufacturers(
        app.customers.expectCurrent,
        manufacturerIds,
      );
    }
  }

  if (!StringUtils.isNullOrEmpty(assemblySelected)) {
    // if you are on a mini tree get the manufacturer ids from the already filtered manufacturers
    if (!filterSelected) {
      const allFilteredManufacturers = await filterManufacturersForExport(
        app.customers.expectCurrent,
        mode,
        app.tree.partsOfSearchedAssembly,
      );
      manufacturers = allFilteredManufacturers;
    }
    if (filterSelected) {
      // if there a filter selected on the table head
      manufacturerIds = app.filterHeaderTable.MPNIds;
      manufacturers = await getManufacturers(
        app.customers.expectCurrent,
        manufacturerIds,
      );
    }
  }
  // }

  app.table.sort(manufacturers, "manufacturer");
  const partsIds = manufacturers.map((m) => m.partNumber);
  const parts = await getParts(app.customers.expectCurrent, partsIds);
  const { data, fields } = getManufacturerDataBasedOnExportType(
    app,
    manufacturers,
    parts,
    false,
    exportType,
  );

  let sheet: {}[] = [];
  if (exportType === "custom") {
    sheet = [
      fields.map(
        (field) => `${app.field.getLabel(field)}\n-${field.split(".")[1]}-`,
      ),
      ...data,
    ];
  } else {
    sheet = [fields.map((field) => app.field.getExportLabel(field)), ...data];
  }
  const worksheet = XLSX.utils.json_to_sheet(sheet, {
    skipHeader: true,
    dateNF: "yyyy-mm-dd",
  });

  applyStatusColors(app, worksheet, fields, data.length);

  // set width cell in "characters"
  const columnWidth = 20;
  const wscols = data[0]
    ? Object.keys(data[0]).map(() => ({ wch: columnWidth }))
    : [];
  worksheet["!cols"] = wscols;

  return worksheet;
}

/** Exports the RM data as seen in the bill of materials tables from a part */
async function getExportedParts(app: AppType, parts: Partial<Part>[]) {
  let fields: string[] = [];
  let sheet: {}[] = [];
  const data: {}[] = [];

  if (Number(app.partsTable.size) !== parts.length && app.view === "RM") {
    /** retrieve all data needed for the export if there is no filter applied on the list */
    parts = await getPartsBasedOnView(
      app,
      app.RM.mode,
      app.customers.expectCurrent,
    );
  }

  if (app.RM.isPart) {
    fields = app.list.part.csvExportPartsListColumns;
  } else {
    fields = app.list.part.csvExportPartColumns;
  }

  if (
    app.customers.expectCurrent === Customer.MTU &&
    app.RM.mode == "parts-status"
  ) {
    fields = setMTUExportColumns(app);
  }

  app.table.sort(parts, "part");
  parts.forEach((part) => {
    data.push(fields.map((field) => getValues(app, field, part)));
  });
  sheet = [fields.map((field) => app.field.getExportLabel(field)), ...data];
  const worksheet = XLSX.utils.json_to_sheet(sheet, {
    skipHeader: true,
    dateNF: "yyyy-mm-dd",
  });

  applyStatusColors(app, worksheet, fields, data.length);

  // set width cell in "characters"
  const columnWidth = 20;
  const wscols = data[0]
    ? Object.keys(data[0]).map(() => ({ wch: columnWidth }))
    : [];
  worksheet["!cols"] = wscols;

  return worksheet;
}

/** Exports the RAD "Proactive Items" table (all proactive parts under the assembly) */
async function getExportedProactiveItems(app: AppType) {
  const parts = await getPartsOfSelectedAssembly(app, "proactiveItems");

  return getPartsWorksheet(
    app,
    parts,
    app.list.part.csvExportProactiveItemsColumns,
  );
}

/** Exports the RAD "Engineering" table (every part under the assembly, proactive or not) */
async function getExportedEngineeringParts(app: AppType) {
  const parts = await getPartsOfSelectedAssembly(app, "engineering");

  return getPartsWorksheet(
    app,
    parts,
    app.list.part.csvExportEngineeringColumns,
  );
}

/** The rows one of the assembly views holds: while the table head has filters set the export
 * holds the filtered result set only, the same way the other parts tables export theirs */
async function getPartsOfSelectedAssembly(app: AppType, viewType: string) {
  const filterValues = app.filterHeaderTable.filterValues;
  const assemblySelected = app.filterTree.assemblySelected;

  if (Object.keys(filterValues).length > 0) {
    return await filterPartsForExport(
      app.customers.expectCurrent,
      filterValues,
      viewType,
      assemblySelected,
    );
  }

  const { parts } =
    viewType === "proactiveItems"
      ? await getProactiveItems(app.customers.expectCurrent, assemblySelected)
      : await getEngineeringParts(app.customers.expectCurrent, assemblySelected);

  return parts;
}

/** One row per part, holding the given fields in the order the report lists them */
function getPartsWorksheet(
  app: AppType,
  parts: Partial<Part>[],
  fields: string[],
) {
  app.table.sort(parts, "part");
  const data = parts.map((part) =>
    fields.map((field) => getValues(app, field, part)),
  );
  const sheet = [
    fields.map((field) => app.field.getExportLabel(field)),
    ...data,
  ];
  const worksheet = XLSX.utils.json_to_sheet(sheet, {
    skipHeader: true,
    dateNF: "yyyy-mm-dd",
  });

  applyStatusColors(app, worksheet, fields, data.length);

  const columnWidth = 20;
  worksheet["!cols"] = data[0]
    ? Object.keys(data[0]).map(() => ({ wch: columnWidth }))
    : [];

  return worksheet;
}

function setMTUExportColumns(app: AppType) {
  let specialColumns = [
    app.fieldId.part.actualStock,
    app.fieldId.part.stockPAS,
    app.fieldId.part.stockMS5,
    app.fieldId.part.stockPS2,
    app.fieldId.part.electronicalMat,
    app.fieldId.part.productHierarchy,
    app.fieldId.part.lastNote,
  ];

  specialColumns = [
    ...app.list.part.csvExportPartsListColumns,
    ...specialColumns,
  ];

  return specialColumns;
}

/** Exports the RM data as seen in the bill of materials from a part + manufacturer tables */
async function getExportedBillOfMatWithManufacturers(
  app: AppType,
  subPartsWithManufacturers: Part[],
  subPartsWithParts: Part[],
  exportType?: string,
) {
  const subPartIds = subPartsWithManufacturers.map((p) => p.partNumber);
  const manufacturers = await getManufacturersByCPNs(
    app.customers.expectCurrent,
    subPartIds,
  );

  const { data, fields } = getManufacturerDataBasedOnExportType(
    app,
    manufacturers,
    subPartsWithManufacturers,
    true,
    exportType,
  );

  subPartsWithParts.forEach((part) => {
    data.push(fields.map((field) => getValues(app, field, part)));
  });

  let sheet: {}[] = [];
  sheet = [fields.map((field) => app.field.getExportLabel(field)), ...data];
  const worksheet = XLSX.utils.json_to_sheet(sheet, {
    skipHeader: true,
    dateNF: "yyyy-mm-dd",
  });

  applyStatusColors(app, worksheet, fields, data.length);

  // set width cell in "characters"
  const columnWidth = 20;
  const wscols = data[0]
    ? Object.keys(data[0]).map(() => ({ wch: columnWidth }))
    : [];
  worksheet["!cols"] = wscols;

  return worksheet;
}

/** Get manufacturer data based on exportType */
function getManufacturerDataBasedOnExportType(
  app: AppType,
  manufacturers: Manufacturer[],
  parts: Part[],
  fromBillOfMatPage: boolean,
  exportType?: string,
) {
  switch (exportType) {
    case "substances":
      const csvExportReachSubstancesFields =
        app.list.manufacturer.csvExportReachSubstances;

      // Remove quantity column if export is made from the manufacturers table
      if (!fromBillOfMatPage) {
        const index = app.list.manufacturer.csvExportReachSubstances.indexOf(
          app.fieldId.part.quantity,
        );
        csvExportReachSubstancesFields.splice(index, 1);
      }
      return {
        data: getManufacturersSubstancesDetails(
          app,
          manufacturers,
          parts,
          csvExportReachSubstancesFields,
        ),
        fields: csvExportReachSubstancesFields,
      };
    case "billOfMaterial":
      const csvExportBillOfMaterialColumns =
        app.list.part.csvExportBillOfMaterialColumns;

      return {
        data: getManufacturersGeneralDetails(
          app,
          manufacturers,
          parts,
          csvExportBillOfMaterialColumns,
        ),
        fields: csvExportBillOfMaterialColumns,
      };
    case "custom":
      return {
        data: getManufacturersGeneralDetails(
          app,
          manufacturers,
          parts,
          app.list.manufacturer.csvExportManufacturerCustomColumns,
        ),
        fields: app.list.manufacturer.csvExportManufacturerCustomColumns,
      };
    default:
      const csvExportManufacturerColumns = [
        ...app.list.manufacturer.csvExportManufacturerColumns,
      ];

      const countryOfOriginIndex = csvExportManufacturerColumns.indexOf(
        app.fieldId.manufacturer.countryOfOrigin,
      );
      if (countryOfOriginIndex !== -1) {
        const existingManufacturingLocationFields = [
          app.fieldId.manufacturer.manufacturingLocations,
        ];
        existingManufacturingLocationFields.forEach((field) => {
          const fieldIndex = csvExportManufacturerColumns.indexOf(field);
          if (fieldIndex === -1) {
            return;
          }
          csvExportManufacturerColumns.splice(
            fieldIndex,
            1,
          );
        });
        csvExportManufacturerColumns.splice(
          countryOfOriginIndex + 1,
          0,
          app.fieldId.manufacturer.manufacturingLocations,
        );
      }

      // Remove quantity column if export is made from the manufacturers table
      if (!fromBillOfMatPage) {
        const index = csvExportManufacturerColumns.indexOf(
          app.fieldId.part.quantity,
        );
        csvExportManufacturerColumns.splice(index, 1);
      }
      return {
        data: getManufacturersGeneralDetails(
          app,
          manufacturers,
          parts,
          csvExportManufacturerColumns,
        ),
        fields: csvExportManufacturerColumns,
      };
  }
}

/** Special Case. When a file has been manually uploaded to the conformityCertificate field */
function changeValueOfTheField(app: AppType, manufacturer: Manufacturer) {
  if (manufacturer.attachmentHistory) {
    const files = manufacturer.attachmentHistory;
    if (files == null) {
      return;
    }
    const fileNames = Object.keys(files);
    fileNames.forEach((name) => {
      const fieldId = files[name].fieldId;
      if (
        fieldId === app.fieldId.manufacturer.conformityCertificate.split(".")[1]
      ) {
        manufacturer.conformityCertificate =
          app.text.manufacturer.manuallyUpload;
      }
    });
  }
  return manufacturer;
}

function getManufacturersGeneralDetails(
  app: AppType,
  manufacturers: Manufacturer[],
  parts: Part[],
  fields: string[],
) {
  const data: {}[] = [];

  manufacturers.forEach((manufacturer) => {
    const part = parts.find((p) => p._id === manufacturer.partNumber);

    if (part == null) {
      return;
    }

    if (manufacturer["obsolescenceStatusOverride"]) {
      const newManufacturer = changeValueOfTheField(app, manufacturer);
      if (newManufacturer == null) {
        return;
      } else {
        manufacturer = newManufacturer;
      }
    }
    data.push(
      fields.map((field) => {
        if (field === app.fieldId.manufacturer.manufacturingLocations) {
          return Array.isArray(manufacturer.manufacturingLocations)
            ? manufacturer.manufacturingLocations
                .map((location) =>
                  [
                    location.Facilitytype === "Fabrication"
                      ? app.text.manufacturer.countryOfDiffusionLocation
                      : location.Facilitytype === "IC Assembly" ||
                          location.Facilitytype === "Factory" ||
                          location.Facilitytype === "Assembly"
                        ? app.text.manufacturer.countryOfAssemblyLocation
                        : location.Facilitytype === "IC Test" ||
                            location.Facilitytype === "Module Test" ||
                            location.Facilitytype === "Wafer Test"
                          ? app.text.manufacturer.countryOfTestingLocation
                          : "",
                    location.CountryName,
                    location.CityName,
                    location.Address,
                  ]
                    .filter((value) => value != null && value !== "")
                    .join(", "),
                )
                .join("; ")
            : "";
        }

        return getValues(
          app,
          field,
          field.split(".")[0] === "manufacturer" ? manufacturer : part,
        );
      }),
    );
  });
  return data;
}

function getManufacturersSubstancesDetails(
  app: AppType,
  manufacturers: Manufacturer[],
  parts: Part[],
  fields: string[],
) {
  const data: {}[] = [];

  manufacturers.forEach((manufacturer) => {
    const part = parts.find((p) => p._id === manufacturer.partNumber);

    if (part == null) {
      return;
    }

    let svhcItems = manufacturer.svhcItems;
    if (
      svhcItems == null ||
      (typeof svhcItems === "string" && svhcItems === "")
    ) {
      svhcItems = [];
    } else {
      svhcItems = manufacturer.svhcItems;
    }

    if (svhcItems && svhcItems.length > 0) {
      svhcItems.forEach((item: SVHCItem) => {
        data.push(
          fields.map((field) => {
            if (
              field === "manufacturer.substanceIdentification" ||
              field === "manufacturer.casNumber" ||
              field === "manufacturer.concentration" ||
              field === "manufacturer.thresholdLimitExceeded"
            ) {
              return getValueOfTheField(field, item);
            } else {
              return getValues(
                app,
                field,
                field.split(".")[0] === "manufacturer" ? manufacturer : part,
              );
            }
          }),
        );
      });
    } else {
      data.push(
        fields.map((field) =>
          getValues(
            app,
            field,
            field.split(".")[0] === "manufacturer" ? manufacturer : part,
          ),
        ),
      );
    }
  });
  return data;
}

async function getExportedAlerts(app: AppType, docType: string) {
  let sheet: {}[] = [];
  const { data, fields } = getExportedAlertsData(app, docType);
  sheet = [fields.map((field) => app.field.getExportLabel(field)), ...data];
  const worksheet = XLSX.utils.json_to_sheet(sheet, {
    skipHeader: true,
    dateNF: "yyyy-mm-dd",
  });

  applyStatusColors(app, worksheet, fields, data.length);

  // set width cell in "characters"
  const columnWidth = 20;
  const wscols = data[0]
    ? Object.keys(data[0]).map(() => ({ wch: columnWidth }))
    : [];
  worksheet["!cols"] = wscols;

  return worksheet;
}

function getExportedAlertsData(app: AppType, docType: string) {
  let csvColumns: string[] = [];
  switch (docType) {
    case Alert.CHANGEALERT:
      csvColumns = app.list.alert.changeAlertsCsvExportColumns;
      break;
    case Alert.LEADTIMEALERT:
      csvColumns = app.list.alert.leadTimeAlertCsvExportColumns;
      break;
    case Alert.INVENTORYMONITORINGALERT:
      csvColumns = app.list.alert.invMonAlertCsvExportColumns;
      break;
    case Alert.ENVCOMPLIANCEALERT:
      csvColumns = app.list.alert.envComplianceAlertCsvExportColumns;
      break;
    default:
      csvColumns = app.list.alert.alertsCsvExportColumns;
      break;
  }
  return {
    data: getExportedAlertsDetails(app, app.alerts.allAlerts, csvColumns),
    fields: csvColumns,
  };
}

function getExportedAlertsDetails(
  app: AppType,
  alerts: AlertData[],
  fields: string[],
) {
  const data: {}[] = [];
  alerts.forEach((alert: AlertData) => {
    data.push(fields.map((field) => getValues(app, field, alert)));
  });
  return data;
}

async function getExportedTasks(app: AppType) {
  let sheet: {}[] = [];
  const { data, fields } = getExportedTasksData(app);
  sheet = [fields.map((field) => getTaskExportLabel(app, field)), ...data];
  const worksheet = XLSX.utils.json_to_sheet(sheet, {
    skipHeader: true,
    dateNF: "yyyy-mm-dd",
  });

  // set width cell in "characters"
  const columnWidth = 20;
  const wscols = data[0]
    ? Object.keys(data[0]).map(() => ({ wch: columnWidth }))
    : [];
  worksheet["!cols"] = wscols;

  return worksheet;
}

function getTaskExportLabel(app: AppType, field: string) {
  if (field === app.fieldId.post.completedAt) {
    return app.text.post.closedOnDate;
  }

  return app.field.getExportLabel(field);
}

function getExportedTasksData(app: AppType) {
  let csvColumns: string[] = [];
  csvColumns = app.list.post.tasksCsvExportColumns;
  return {
    data: getExportedTasksDetails(app, app.tasks.tasks, csvColumns),
    fields: csvColumns,
  };
}
function getExportedTasksDetails(
  app: AppType,
  tasks: Task[],
  fields: string[],
) {
  const data: {}[] = [];
  tasks.forEach((alert: Task) => {
    data.push(fields.map((field) => getValues(app, field, alert)));
  });
  return data;
}

async function getExportedAuditLogs(app: AppType) {
  let sheet: {}[] = [];
  const { data, fields } = getExportAuditData(app);
  sheet = [fields.map((field) => app.field.getExportLabel(field)), ...data];
  const worksheet = XLSX.utils.json_to_sheet(sheet, {
    skipHeader: true,
    dateNF: "yyyy-mm-dd , hh:mm:ss",
  });

  // set width cell in "characters"
  const columnWidth = 20;
  const wscols = data[0]
    ? Object.keys(data[0]).map(() => ({ wch: columnWidth }))
    : [];
  worksheet["!cols"] = wscols;

  return worksheet;
}

function getExportAuditData(app: AppType) {
  let csvColumns: string[] = [];
  csvColumns = app.list.auditLog.auditColumns;
  return {
    data: getExportedAuditDetails(app, app.operations.auditLogs, csvColumns),
    fields: csvColumns,
  };
}

function getExportedAuditDetails(
  app: AppType,
  auditLogs: AuditLog[],
  fields: string[],
) {
  const data: {}[] = [];
  auditLogs.forEach((auditLog: AuditLog) => {
    data.push(fields.map((field) => getValues(app, field, auditLog)));
  });
  return data;
}

async function getExportedPosts(app: AppType) {
  let sheet: {}[] = [];
  const { data, fields } = getExportedPostsData(app);
  sheet = [fields.map((field) => app.field.getExportLabel(field)), ...data];
  const worksheet = XLSX.utils.json_to_sheet(sheet, {
    skipHeader: true,
    dateNF: "yyyy-mm-dd",
  });

  // set width cell in "characters"
  const columnWidth = 20;
  const wscols = data[0]
    ? Object.keys(data[0]).map(() => ({ wch: columnWidth }))
    : [];
  worksheet["!cols"] = wscols;

  return worksheet;
}

function getExportedPostsData(app: AppType) {
  let csvColumns: string[] = [];
  const clonePosts: Post[] = [];

  app.post.posts.forEach((post) => clonePosts.push(Object.assign({}, post)));
  csvColumns = app.list.post.postsCsvExportColumns;

  return {
    data: getExportedPostsDetails(app, clonePosts, csvColumns),
    fields: csvColumns,
  };
}

function getExportedPostsDetails(
  app: AppType,
  posts: Post[],
  fields: string[],
) {
  const data: {}[] = [];

  posts.forEach((post: Post) => {
    // Status of the case
    post.omfStatus = post.snapshot?.thread?.omfStatus;

    // Special case for displaying the solution type on title_comment
    if (post.typeOfPost === SOLUTION && post.resolveClass != undefined) {
      post.title_comment = app.field.getFieldValueAsText(
        "post.resolveClass",
        post.resolveClass,
      );
    }

    // Special case for displaying the taskDescription, taskNote on title_comment respectively content
    if (post.typeOfPost === TASK) {
      if (post.taskDescription != undefined) {
        post.title_comment = app.field.getFieldValueAsText(
          "post.taskDescription",
          post.taskDescription,
        );
      }
      if (post.taskNote != undefined) {
        post.content = app.field.getFieldValueAsText(
          "post.taskNote",
          post.taskNote,
        );
      }
    }

    // Creator of the post
    if (post.user_id != undefined) {
      post.user_id = app.users.getOptionName(post.user_id);
    }

    // Capitalize typeOfPost
    post.typeOfPost =
      post.typeOfPost[0].toUpperCase() + post.typeOfPost.slice(1);

    data.push(fields.map((field) => getValues(app, field, post)));
  });
  return data;
}

async function getExportedUsers(app: AppType) {
  let sheet: {}[] = [];
  const { data, fields } = getExportedUsersData(app);
  sheet = [fields.map((field) => app.field.getExportLabel(field)), ...data];
  const worksheet = XLSX.utils.json_to_sheet(sheet, {
    skipHeader: true,
    dateNF: "yyyy-mm-dd",
  });

  // set width cell in "characters"
  const columnWidth = 20;
  const wscols = data[0]
    ? Object.keys(data[0]).map(() => ({ wch: columnWidth }))
    : [];
  worksheet["!cols"] = wscols;

  return worksheet;
}

function getExportedUsersData(app: AppType) {
  let csvColumns: string[] = [];
  csvColumns = app.list.user.usersCsvExportColumns;
  if (app.customers.expectCurrent === Customer.NS) {
    if (app.auth.isTeam) {
      csvColumns = csvColumns.filter((x) => x !== "user.passwordExpireDate");
    } else {
      csvColumns = csvColumns.filter((x) => x !== "user.jobTitle");
    }
  }

  return {
    data: getExportedUsersDetails(app, app.users.users, csvColumns),
    fields: csvColumns,
  };
}

function getExportedUsersDetails(
  app: AppType,
  users: UserCompressed[],
  fields: string[],
) {
  const data: {}[] = [];
  users.forEach((user: any) => {
    const userCopy = Object.assign({}, user);
    if (userCopy.active) {
      userCopy.active = "Active";
    } else {
      userCopy.active = "Offline";
    }

    if (userCopy.roles != null) {
      userCopy.roles.forEach((role: string) => {
        const userRole = role.split("-")[1];
        const userRoleText = app.field.getOptionText("user.roles", userRole);
        userCopy.roles = [userRoleText];
      });
    }

    if (app.customers.expectCurrent === Customer.NS) {
      if (app.auth.isTeam) {
        fields = fields.filter((x) => x !== "user.passwordExpireDate");
      } else {
        fields = fields.filter((x) => x !== "user.jobTitle");
      }
    }

    if (app.customers.expectCurrent === Customer.NS && app.auth.isTeam) {
    }
    data.push(fields.map((field) => getValues(app, field, userCopy)));
  });
  return data;
}

function getValueOfTheField(field: string, item: SVHCItem) {
  let result = "";
  field = field.split(".")[1];
  switch (field) {
    case "substanceIdentification":
    case "casNumber":
    case "concentration":
      result = item[field] as string;
      break;
    case "thresholdLimitExceeded":
      if (item.concentration === "") {
        result = "";
      } else {
        result = item[field] === true ? "Yes" : "No";
      }
      break;
  }
  return result;
}

function getValues(app: AppType, fieldId: string, doc: any) {
  const [type, field] = fieldId.split(".");
  if (doc != null && doc[field] != null && doc[field] != "null") {
    // special case since same fields exists on the part and also on the manufacturer side
    if (type === "manufacturer" && doc.type === "part") {
      return "";
    } else {
      if (fieldId === "part.lastNote") {
        return doc[field];
      } else {
        const fieldSettings = app.field.getFieldSettings(fieldId);
        // The table header filters switch the date columns (e.g. estimatedEOLDate) from
        // "timestamp" to "dateInterval" while the table is shown, so both are dates here
        if (
          fieldSettings.type === FieldTypes.timestamp ||
          fieldSettings.type === FieldTypes.dateInterval ||
          fieldId === "manufacturer.lastUpdatedOn"
        ) {
          if (doc[field] != null && doc[field] > 0) {
            // Date-only fields (issue/effective/EOL/LTB dates, ...) are shown in
            // the app in UTC (`| date:'yyyy-MM-dd':'UTC'`). Build the exported
            // Date from the value's UTC parts so Excel shows the same calendar
            // day. Using the local wall-clock (formatDatetime) shifted these
            // dates by +1 day in positive-offset timezones. Datetime fields
            // (update_time, create_time, ...) stay local to match their display.
            if (app.table.getColumnTypeDisplay(field) === "date") {
              const utc = new Date(doc[field]);
              return new Date(
                utc.getUTCFullYear(),
                utc.getUTCMonth(),
                utc.getUTCDate(),
                utc.getUTCHours(),
                utc.getUTCMinutes(),
                utc.getUTCSeconds(),
              );
            }
            return new Date(formatDatetime(doc[field]));
          } else {
            return "";
          }
        }

        if (field === "finalSolution") {
          return app.field.getOptionText(
            app.fieldId.post.resolveClass,
            doc[field],
          );
        }
        if (field === "userId") {
          return app.users.getOptionName(doc[field]);
        }
        // if (field === 'auditLog.time') {
        //    const date = new Date(formatDatetime(doc[field])
        // }

        // withoutData is a filter value, so it is not part of the options of the
        // field and getFieldValueAsText would fall back to the stored value
        if (doc[field] === WITHOUT_DATA) {
          return app.text.RM.withoutData;
        }

        return app.field.getFieldValueAsText(fieldId, doc[field]);
      }
    }
  } else {
    return "";
  }
}

function generateExportNameForParts(app: AppType, part: Part) {
  let name = generateRMExportFileName(app);

  if (!app.RM.isPart) {
    const partNumber = part[app.fieldId.part.partNumber];
    const partDescr = part[app.fieldId.part.description];

    if (partDescr != null) {
      name = `${partNumber}_${partDescr}.xlsx`;
    } else {
      name = `${partNumber}.xlsx`;
    }
  } else if (app.customers.expectCurrent === Customer.NS) {
    const partNumber = app.model[app.fieldId.part.partNumber];
    if (!StringUtils.isNullOrEmpty(partNumber)) {
      name = `${partNumber}.xlsx`;
    }
  }
  return name;
}

/** The name of a report holding the parts of one assembly, e.g. "Client Report_A-100.xlsx" */
function generateAssemblyReportFileName(app: AppType, label: string) {
  const assemblySelected = app.filterTree.assemblySelected;

  if (!StringUtils.isNullOrEmpty(assemblySelected)) {
    return `${label}_${assemblySelected}.xlsx`;
  }
  return `${label}.xlsx`;
}

async function getPartsBasedOnView(
  app: AppType,
  view: string,
  customer: string
) {
  switch (view) {
    case "parts-to-be-deleted":
      return await getPartsToBeDeletedAll(customer);
    case "without-manufacturers":
      return await getPartsWithoutManufacturersAll(customer);
    case "not-used-assemblies":
      return await getPartsNotUsedInAssembliesAll(customer);
    case "parts-status":
      return await getAllPartsByStatus(customer);
    case "all-parts":
      // let result = await getPartsCompressed(customer);
      const result = await filterPartsForExport(
        app.customers.expectCurrent,
        app.filterHeaderTable.filterValues,
        app.RM.mode
      );
      return result;
    default:
      return [];
  }
}

function generateRMExportFileName(app: AppType, exportType?: string) {
  let fileName = "";
  const assemblySelected = app.filterTree.assemblySelected;

  // Format curent date
  const today = new Date();
  const formattedDate = today.toISOString().slice(0, 10).replace(/-/g, "");

  switch (app.RM.mode) {
    case "all-manufacturers":
      fileName = app.text.app.allManufacturers;

      // Export is made on all manufacturers table after a RM Search is performed
      if (!StringUtils.isNullOrEmpty(assemblySelected)) {
        if (app.customers.expectCurrent === Customer.NS) {
          fileName = `${assemblySelected}`;
        }

        if (
          app.customers.expectCurrent === Customer.AUTOFLUG &&
          exportType === "custom"
        ) {
          fileName = `${app.text.manufacturer.customExport}_${assemblySelected}_LCM_${formattedDate}`;
        }
      } else {
        // Export is made on all manufacturers table from Risk Management
        if (
          app.customers.expectCurrent === Customer.AUTOFLUG &&
          exportType === "custom"
        ) {
          fileName = `${app.text.manufacturer.customExport}_LCM_${formattedDate}`;
        }
      }
      break;
    case "matched":
      fileName = app.text.app.matchedManufacturers;
      break;
    case "unmatched":
      fileName = app.text.app.notMatchedManufacturers;
      break;
    case "manual":
      fileName = app.text.app.manualAssignmentManufacturers;
      break;
    case "override":
      fileName = app.text.app.manualOverrideManufacturers;
      break;
    case "manualAssignment":
      fileName = app.text.app.editedManufacturers;
      break;
    case "linkedToDeletedCPN":
      fileName = app.text.app.toDeletedManufacturers;
      break;
    case "engineering":
      fileName = app.text.app.allManufacturers;
      break;
    case "all-parts":
      fileName = app.text.app.allParts;
      break;
    case "without-manufacturers":
      fileName = app.text.app.partsWithoutMPNs;
      break;
    case "not-used-assemblies":
      fileName = app.text.app.partsNotUsed;
      break;
    case "parts-to-be-deleted":
      fileName = app.text.app.toDeletedParts;
      break;
    default:
      fileName = "report";
  }

  return `${fileName}.xlsx`;
}

function generateAlertsExportFileName(app: AppType, docType: string) {
  let fileName = "";
  switch (docType) {
    case Alert.ALERT:
      fileName = app.text.alert.allAlertsExport;
      break;
    case Alert.CHANGEALERT:
      fileName = app.text.alert.allChangesExport;
      break;
    case Alert.LEADTIMEALERT:
      fileName = app.text.alert.allLeadTimeAlertsExport;
      break;
    case Alert.INVENTORYMONITORINGALERT:
      fileName = app.text.alert.allInvMonitoringAlertsExport;
      break;
    case Alert.ENVCOMPLIANCEALERT:
      fileName = app.text.alert.envComplianceExport;
      break;
    default:
      fileName = "report";
  }

  let name = `${fileName}.xlsx`;
  return name;
}

function generateOMExportFileName(app: AppType) {
  let fileName = "";
  switch (app.tasks.tasksTab) {
    case "myOpenTasks":
      fileName = app.text.app.myTasksExport;
      break;
    case "createdOpenTasks":
      fileName = app.text.app.createdTasks;
      break;
    case "allOpenTasks":
      fileName = app.text.app.allTasks;
      break;
    default:
      fileName = "report";
  }

  let name = `${fileName}.xlsx`;
  return name;
}
